/**
 * Shopify Order Upload System with Dropbox Integration
 * Handles file uploads, order creation, and Dropbox API integration
 */

class OrderUploadSystem {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.section = document.querySelector(`[data-section-id="${sectionId}"]`);
    this.dropboxToken = localStorage.getItem('dropbox_token') || '';
    this.files = [];
    this.orders = JSON.parse(localStorage.getItem('order_history') || '[]');
    this.isUploading = false;
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateAuthStatus();
    this.renderOrderHistory();
    this.checkForOAuthCallback();
  }

  bindEvents() {
    // Dropbox authentication
    this.section.querySelector('#connect-dropbox')?.addEventListener('click', () => this.connectDropbox());
    this.section.querySelector('#demo-mode')?.addEventListener('click', () => this.enableDemoMode());
    this.section.querySelector('#disconnect-dropbox')?.addEventListener('click', () => this.disconnectDropbox());

    // File upload
    const uploadZone = this.section.querySelector('#upload-zone');
    const fileInput = this.section.querySelector('#file-input');

    uploadZone?.addEventListener('click', () => fileInput?.click());
    uploadZone?.addEventListener('dragover', (e) => this.handleDragOver(e));
    uploadZone?.addEventListener('dragleave', (e) => this.handleDragLeave(e));
    uploadZone?.addEventListener('drop', (e) => this.handleDrop(e));
    fileInput?.addEventListener('change', (e) => this.handleFileSelect(e));

    // Order form
    this.section.querySelector('#customer-form')?.addEventListener('submit', (e) => this.handleOrderSubmit(e));
  }

  checkForOAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      this.showError(`Authentication failed: ${error}`);
      this.cleanupUrl();
      return;
    }

    if (code && !this.dropboxToken) {
      this.handleOAuthCallback(code);
    }
  }

  async handleOAuthCallback(code) {
    this.showLoading('Authenticating with Dropbox...');

    try {
      const token = await this.exchangeCodeForToken(code);
      this.setDropboxToken(token);
      this.cleanupUrl();
      this.hideLoading();
      this.updateAuthStatus();
    } catch (error) {
      this.hideLoading();
      this.showError('Failed to authenticate with Dropbox. Please try again.');
      console.error('OAuth callback error:', error);
    }
  }

  async exchangeCodeForToken(code) {
    const response = await fetch('https://api.dropboxapi.com/oauth2/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        grant_type: 'authorization_code',
        client_id: window.DROPBOX_CONFIG.APP_KEY,
        client_secret: window.DROPBOX_CONFIG.APP_SECRET,
        redirect_uri: window.DROPBOX_CONFIG.REDIRECT_URI
      })
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for token');
    }

    const data = await response.json();
    return data.access_token;
  }

  connectDropbox() {
    const params = new URLSearchParams({
      client_id: window.DROPBOX_CONFIG.APP_KEY,
      response_type: 'code',
      redirect_uri: window.DROPBOX_CONFIG.REDIRECT_URI,
      scope: 'files.content.write files.content.read sharing.write'
    });
    
    const authUrl = `https://www.dropbox.com/oauth2/authorize?${params.toString()}`;
    window.location.href = authUrl;
  }

  enableDemoMode() {
    this.setDropboxToken('demo-token');
    this.updateAuthStatus();
  }

  disconnectDropbox() {
    this.setDropboxToken('');
    this.files = [];
    this.updateFileList();
    this.updateAuthStatus();
  }

  setDropboxToken(token) {
    this.dropboxToken = token;
    if (token) {
      localStorage.setItem('dropbox_token', token);
    } else {
      localStorage.removeItem('dropbox_token');
    }
  }

  async updateAuthStatus() {
    const authStatus = this.section.querySelector('#dropbox-auth-status');
    const authRequired = this.section.querySelector('#dropbox-auth-required');
    const uploadInterface = this.section.querySelector('#upload-interface');

    if (this.dropboxToken) {
      authRequired?.classList.add('hidden');
      authStatus?.classList.remove('hidden');
      uploadInterface?.classList.remove('hidden');

      if (this.dropboxToken === 'demo-token') {
        const accountInfo = this.section.querySelector('#dropbox-account-info');
        if (accountInfo) {
          accountInfo.textContent = 'Demo Mode - Files will be simulated';
        }
      } else {
        try {
          const accountInfo = await this.getAccountInfo();
          const accountInfoEl = this.section.querySelector('#dropbox-account-info');
          if (accountInfoEl && accountInfo) {
            accountInfoEl.textContent = `Connected as ${accountInfo.name?.display_name || accountInfo.email}`;
          }
        } catch (error) {
          console.error('Failed to get account info:', error);
        }
      }
    } else {
      authStatus?.classList.add('hidden');
      uploadInterface?.classList.add('hidden');
      authRequired?.classList.remove('hidden');
    }
  }

  async getAccountInfo() {
    if (this.dropboxToken === 'demo-token') return null;

    const response = await fetch('https://api.dropboxapi.com/2/users/get_current_account', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.dropboxToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get account info');
    }

    return await response.json();
  }

  handleDragOver(e) {
    e.preventDefault();
    e.currentTarget.classList.add('dragover');
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
  }

  handleDrop(e) {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      this.uploadFiles(files);
    }
  }

  handleFileSelect(e) {
    const files = e.target.files;
    if (files && files.length > 0) {
      this.uploadFiles(files);
    }
  }

  async uploadFiles(fileList) {
    if (this.isUploading) return;

    this.isUploading = true;
    const filesArray = Array.from(fileList);
    
    // Add files to the list with uploading status
    const newFiles = filesArray.map(file => ({
      id: this.generateId(),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date(),
      status: 'uploading',
      progress: 0,
      file: file
    }));

    this.files.push(...newFiles);
    this.updateFileList();

    // Upload each file
    for (const fileData of newFiles) {
      try {
        const dropboxUrl = await this.uploadToDropbox(fileData.file, (progress) => {
          fileData.progress = Math.round(progress);
          this.updateFileProgress(fileData.id, progress);
        });
        
        fileData.status = 'completed';
        fileData.dropboxUrl = dropboxUrl;
        fileData.progress = 100;
        this.updateFileStatus(fileData.id, 'completed', dropboxUrl);
      } catch (error) {
        console.error('File upload failed:', error);
        fileData.status = 'error';
        fileData.progress = 0;
        this.updateFileStatus(fileData.id, 'error');
      }
    }
    
    this.isUploading = false;
    this.updateOrderForm();
  }

  async uploadToDropbox(file, onProgress) {
    if (this.dropboxToken === 'demo-token') {
      // Simulate upload progress for demo
      return new Promise((resolve) => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += Math.random() * 20;
          if (progress >= 100) {
            progress = 100;
            clearInterval(interval);
            onProgress(progress);
            resolve(`https://dropbox.com/s/demo-${Date.now()}/${file.name}`);
          } else {
            onProgress(progress);
          }
        }, 200);
      });
    }

    // Real Dropbox upload
    const response = await fetch('https://content.dropboxapi.com/2/files/upload', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.dropboxToken}`,
        'Dropbox-API-Arg': JSON.stringify({
          path: `/order-uploads/${Date.now()}-${file.name}`,
          mode: 'add',
          autorename: true,
          mute: false
        }),
        'Content-Type': 'application/octet-stream'
      },
      body: file
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Upload failed: ${errorData.error_summary || response.statusText}`);
    }

    const result = await response.json();
    return await this.createSharedLink(result.path_display);
  }

  async createSharedLink(path) {
    if (this.dropboxToken === 'demo-token') {
      return `https://dropbox.com/s/demo-${Date.now()}/${path.split('/').pop()}`;
    }

    try {
      const response = await fetch('https://api.dropboxapi.com/2/sharing/create_shared_link_with_settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.dropboxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          path: path,
          settings: {
            requested_visibility: 'public',
            audience: 'public',
            access: 'viewer'
          }
        })
      });

      if (!response.ok) {
        // Try to get existing shared link
        const listResponse = await fetch('https://api.dropboxapi.com/2/sharing/list_shared_links', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.dropboxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            path: path,
            direct_only: true
          })
        });

        if (listResponse.ok) {
          const listResult = await listResponse.json();
          if (listResult.links && listResult.links.length > 0) {
            return listResult.links[0].url;
          }
        }

        throw new Error('Failed to create or retrieve shared link');
      }

      const result = await response.json();
      return result.url;
    } catch (error) {
      console.error('Failed to create shared link:', error);
      throw error;
    }
  }

  updateFileList() {
    const fileList = this.section.querySelector('#file-list');
    const filesContainer = this.section.querySelector('#files-container');
    const fileCount = this.section.querySelector('#file-count');

    if (!filesContainer) return;

    if (this.files.length === 0) {
      fileList?.classList.add('hidden');
      return;
    }

    fileList?.classList.remove('hidden');
    if (fileCount) fileCount.textContent = this.files.length;

    filesContainer.innerHTML = this.files.map(file => `
      <div class="file-item" data-file-id="${file.id}">
        <div class="file-icon ${file.status}">
          ${this.getFileIcon(file.status)}
        </div>
        <div class="file-info">
          <div class="file-name">${file.name}</div>
          <div class="file-meta">
            <span>${this.formatFileSize(file.size)}</span>
            ${file.dropboxUrl ? `<span>•</span><a href="${file.dropboxUrl}" target="_blank" class="file-link">View in Dropbox</a>` : ''}
          </div>
        </div>
        ${file.status === 'uploading' ? `
          <div class="file-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${file.progress || 0}%"></div>
            </div>
            <div class="progress-text">${file.progress || 0}%</div>
          </div>
        ` : ''}
        <button class="remove-file" onclick="orderUploadSystem.removeFile('${file.id}')" ${file.status === 'uploading' ? 'disabled' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    `).join('');
  }

  updateFileProgress(fileId, progress) {
    const fileItem = this.section.querySelector(`[data-file-id="${fileId}"]`);
    if (!fileItem) return;

    const progressFill = fileItem.querySelector('.progress-fill');
    const progressText = fileItem.querySelector('.progress-text');

    if (progressFill) progressFill.style.width = `${progress}%`;
    if (progressText) progressText.textContent = `${Math.round(progress)}%`;
  }

  updateFileStatus(fileId, status, dropboxUrl = null) {
    const file = this.files.find(f => f.id === fileId);
    if (!file) return;

    file.status = status;
    if (dropboxUrl) file.dropboxUrl = dropboxUrl;

    const fileItem = this.section.querySelector(`[data-file-id="${fileId}"]`);
    if (!fileItem) return;

    const fileIcon = fileItem.querySelector('.file-icon');
    const fileMeta = fileItem.querySelector('.file-meta');
    const fileProgress = fileItem.querySelector('.file-progress');

    if (fileIcon) {
      fileIcon.className = `file-icon ${status}`;
      fileIcon.innerHTML = this.getFileIcon(status);
    }

    if (status === 'completed' && dropboxUrl && fileMeta) {
      const sizeSpan = fileMeta.querySelector('span');
      fileMeta.innerHTML = `
        ${sizeSpan ? sizeSpan.outerHTML : `<span>${this.formatFileSize(file.size)}</span>`}
        <span>•</span>
        <a href="${dropboxUrl}" target="_blank" class="file-link">View in Dropbox</a>
      `;
    }

    if (fileProgress && status !== 'uploading') {
      fileProgress.remove();
    }

    this.updateOrderForm();
  }

  removeFile(fileId) {
    this.files = this.files.filter(f => f.id !== fileId);
    this.updateFileList();
    this.updateOrderForm();
  }

  updateOrderForm() {
    const completedFiles = this.files.filter(f => f.status === 'completed');
    const readyFilesCount = this.section.querySelector('#ready-files-count');
    const readyFilesList = this.section.querySelector('#ready-files-list');
    const createOrderBtn = this.section.querySelector('#create-order-btn');

    if (readyFilesCount) readyFilesCount.textContent = completedFiles.length;
    
    if (readyFilesList) {
      readyFilesList.innerHTML = completedFiles.map(file => 
        `<div class="ready-file-item">${file.name}</div>`
      ).join('');
    }

    if (createOrderBtn) {
      createOrderBtn.disabled = completedFiles.length === 0;
    }
  }

  handleOrderSubmit(e) {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const customerName = formData.get('customer-name');
    const customerEmail = formData.get('customer-email');
    
    if (!customerName || !customerEmail) return;

    const completedFiles = this.files.filter(f => f.status === 'completed');
    if (completedFiles.length === 0) return;

    const order = this.createOrder(customerEmail, customerName, completedFiles);
    this.showSuccess(`Order ${order.id} created successfully! Total: $${order.total.toFixed(2)}`);
    
    // Reset form and files
    e.target.reset();
    this.files = [];
    this.updateFileList();
    this.updateOrderForm();
  }

  createOrder(customerEmail, customerName, files) {
    const order = {
      id: `ORD-${Date.now()}`,
      customerEmail,
      customerName,
      files: files.map(f => ({
        id: f.id,
        name: f.name,
        size: f.size,
        type: f.type,
        dropboxUrl: f.dropboxUrl,
        uploadedAt: f.uploadedAt
      })),
      createdAt: new Date(),
      status: 'pending',
      total: files.length * 25 + Math.random() * 100 // Base price per file + variable cost
    };

    this.orders.unshift(order);
    this.saveOrders();
    this.renderOrderHistory();
    
    return order;
  }

  reorder(orderId) {
    const originalOrder = this.orders.find(o => o.id === orderId);
    if (!originalOrder) return;

    // Set files with completed status since they're already uploaded
    this.files = originalOrder.files.map(file => ({
      ...file,
      status: 'completed',
      progress: 100
    }));

    this.updateFileList();
    this.updateOrderForm();
    
    this.showSuccess(`Files from order ${orderId} have been loaded for reorder`);
  }

  renderOrderHistory() {
    const ordersContainer = this.section.querySelector('#orders-container');
    if (!ordersContainer) return;

    if (this.orders.length === 0) {
      ordersContainer.innerHTML = `
        <div class="no-orders">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <p>No orders yet</p>
        </div>
      `;
      return;
    }

    ordersContainer.innerHTML = this.orders.map(order => `
      <div class="order-item">
        <div class="order-header">
          <div class="order-info">
            <h4>${order.id} <span class="order-status ${order.status}">${order.status}</span></h4>
            <div class="order-meta">
              <span>${order.customerName}</span>
              <span>${order.customerEmail}</span>
              <span>${this.formatDate(new Date(order.createdAt))}</span>
            </div>
          </div>
          <button class="reorder-btn" onclick="orderUploadSystem.reorder('${order.id}')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <polyline points="1,4 1,10 7,10"/>
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
            </svg>
            Reorder
          </button>
        </div>
        <div class="order-files">
          <h5>Files (${order.files.length}):</h5>
          <div class="order-file-grid">
            ${order.files.map(file => `
              <div class="order-file-item">
                <span class="order-file-name">${file.name}</span>
                ${file.dropboxUrl ? `
                  <a href="${file.dropboxUrl}" target="_blank" class="view-file-link">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15,3 21,3 21,9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                    View
                  </a>
                ` : ''}
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `).join('');
  }

  saveOrders() {
    localStorage.setItem('order_history', JSON.stringify(this.orders));
  }

  // Utility methods
  generateId() {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(date) {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  getFileIcon(status) {
    switch (status) {
      case 'completed':
        return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M9 12l2 2 4-4"/>
          <circle cx="12" cy="12" r="10"/>
        </svg>`;
      case 'error':
        return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>`;
      default:
        return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14,2 14,8 20,8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <polyline points="10,9 9,9 8,9"/>
        </svg>`;
    }
  }

  showLoading(text = 'Loading...') {
    const overlay = this.section.querySelector('#loading-overlay');
    const loadingText = this.section.querySelector('#loading-text');
    
    if (loadingText) loadingText.textContent = text;
    overlay?.classList.remove('hidden');
  }

  hideLoading() {
    const overlay = this.section.querySelector('#loading-overlay');
    overlay?.classList.add('hidden');
  }

  showSuccess(message) {
    // You can implement a toast notification system here
    alert(message);
  }

  showError(message) {
    // You can implement a toast notification system here
    alert(message);
  }

  cleanupUrl() {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

// Global instance for event handlers
let orderUploadSystem;

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Find all order upload sections and initialize them
  const sections = document.querySelectorAll('.order-upload-section');
  sections.forEach(section => {
    const sectionId = section.getAttribute('data-section-id');
    if (sectionId) {
      orderUploadSystem = new OrderUploadSystem(sectionId);
    }
  });
});